import { supabase } from "@/integrations/supabase/client";
import { type ConstructionStage } from "@/types";

export const constructionStagesAPI = {
  // Get all construction stages for a specific project
  async getProjectStages(projectId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Try to query with order_index, and if it fails, query without it
    try {
      // First attempt with order_index
      const { data, error } = await supabase
        .from('construction_stages')
        .select('*')
        .eq('project_id', projectId)
        .order('order_index', { ascending: true });

      if (error) {
        // If the error is related to order_index not existing, try without ordering
        if (error.message.includes('order_index')) {
          
          
          const { data: dataWithoutOrder, error: errorWithoutOrder } = await supabase
            .from('construction_stages')
            .select('*')
            .eq('project_id', projectId);
            
          if (errorWithoutOrder) {
            return { data: null, error: errorWithoutOrder };
          }
          
          // Transform data from DB format to frontend format
          const transformedData = dataWithoutOrder.map(stage => ({
            id: stage.id,
            name: stage.name,
            completed: stage.completed,
            phase: stage.phase,
            phaseColor: stage.phase_color,
            completedDate: stage.completed_date ? new Date(stage.completed_date) : undefined,
            note: stage.note,
            photoUrl: stage.photo_url,
            orderIndex: null // We don't have order_index
          }));

          return { data: transformedData as ConstructionStage[], error: null };
        }
        
        // If it's a different error, return it
        return { data: null, error };
      }

      // If successful, transform data from DB format to frontend format
      const transformedData = data.map(stage => ({
        id: stage.id,
        name: stage.name,
        completed: stage.completed,
        phase: stage.phase,
        phaseColor: stage.phase_color,
        completedDate: stage.completed_date ? new Date(stage.completed_date) : undefined,
        note: stage.note,
        photoUrl: stage.photo_url,
        orderIndex: stage.order_index
      }));

      return { data: transformedData as ConstructionStage[], error: null };
    } catch (err) {
      
      return { data: null, error: err as Error };
    }
  },

  // Get a single construction stage by ID
  async getStage(id: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('construction_stages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      return { data: null, error };
    }

    // Transform stage data to frontend format
    const transformedStage: ConstructionStage = {
      id: data.id,
      name: data.name,
      completed: data.completed,
      phase: data.phase,
      phaseColor: data.phase_color,
      completedDate: data.completed_date ? new Date(data.completed_date) : undefined,
      note: data.note,
      photoUrl: data.photo_url,
      orderIndex: data.order_index
    };

    return { data: transformedStage, error: null };
  },

  // Create a new construction stage
  async createStage(projectId: string, stageData: Partial<ConstructionStage>) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Insert the stage
    const { data, error } = await supabase
      .from('construction_stages')
      .insert({
        project_id: projectId,
        name: stageData.name,
        completed: stageData.completed || false,
        phase: stageData.phase,
        phase_color: stageData.phaseColor,
        completed_date: stageData.completedDate ? stageData.completedDate.toISOString() : null,
        note: stageData.note,
        photo_url: stageData.photoUrl,
        order_index: stageData.orderIndex
      })
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    return { data: data.id, error: null };
  },

  // Update a construction stage
  async updateStage(id: string, stageData: Partial<ConstructionStage>) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Format the data for update
    const updateData: any = {};
    if (stageData.name !== undefined) updateData.name = stageData.name;
    if (stageData.completed !== undefined) updateData.completed = stageData.completed;
    if (stageData.phase !== undefined) updateData.phase = stageData.phase;
    if (stageData.phaseColor !== undefined) updateData.phase_color = stageData.phaseColor;
    if (stageData.completedDate !== undefined) {
      updateData.completed_date = stageData.completedDate ? stageData.completedDate.toISOString() : null;
    }
    if (stageData.note !== undefined) updateData.note = stageData.note;
    if (stageData.photoUrl !== undefined) updateData.photo_url = stageData.photoUrl;
    if (stageData.orderIndex !== undefined) updateData.order_index = stageData.orderIndex;

    // Update the stage
    const { data, error } = await supabase
      .from('construction_stages')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    return { 
      data: {
        id: data.id,
        name: data.name,
        completed: data.completed,
        phase: data.phase,
        phaseColor: data.phase_color,
        completedDate: data.completed_date ? new Date(data.completed_date) : undefined,
        note: data.note,
        photoUrl: data.photo_url,
        orderIndex: data.order_index
      } as ConstructionStage, 
      error: null 
    };
  },

  // Toggle completion status of a construction stage
  async toggleStageCompletion(id: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    try {
      // First get the current stage
      const { data: stage, error: fetchError } = await supabase
        .from('construction_stages')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) {
        
        return { data: null, error: fetchError };
      }

      // Prepare the update data - only include necessary fields to avoid order_index issues
      const updateData = {
        completed: !stage.completed,
        completed_date: !stage.completed ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      };

      // Toggle the completion status
      const { data, error } = await supabase
        .from('construction_stages')
        .update(updateData)
        .eq('id', id)
        .select();

      if (error) {
        
        return { data: null, error };
      }

      // Check if any rows were returned
      if (!data || data.length === 0) {
        return { data: null, error: new Error('No stage found with the provided ID') };
      }

      const updatedStage = data[0]; // Get the first row

      // If completed, also update the project progress percentage
      if (!stage.completed) { // Was just marked as completed
        try {
          // Get all stages for this project
          const { data: allStages } = await supabase
            .from('construction_stages')
            .select('*')
            .eq('project_id', stage.project_id);

          if (allStages) {
            // Calculate new progress percentage
            const totalStages = allStages.length;
            const completedStages = allStages.filter(s => s.id === id || s.completed).length;
            const progressPercentage = Math.round((completedStages / totalStages) * 100);

            // Update the project progress
            await supabase
              .from('projects')
              .update({
                progress: progressPercentage,
                updated_at: new Date().toISOString()
              })
              .eq('id', stage.project_id);
          }
        } catch (err) {
          
        }
      }

      // Transform response to our ConstructionStage type
      return { 
        data: {
          id: updatedStage.id,
          name: updatedStage.name,
          completed: updatedStage.completed,
          phase: updatedStage.phase,
          phaseColor: updatedStage.phase_color,
          completedDate: updatedStage.completed_date ? new Date(updatedStage.completed_date) : undefined,
          note: updatedStage.note,
          photoUrl: updatedStage.photo_url,
          orderIndex: updatedStage.order_index
        } as ConstructionStage, 
        error: null 
      };
    } catch (err) {
      
      return { data: null, error: err as Error };
    }
  },

  // Add or update a note for a stage
  async updateStageNote(id: string, note: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('construction_stages')
      .update({
        note,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select();

    if (error) {
      return { data: null, error };
    }

    // Check if any rows were returned
    if (!data || data.length === 0) {
      return { data: null, error: new Error('No stage found with the provided ID') };
    }

    return { data: data[0], error: null };
  },

  // Add or update a photo for a stage
  async updateStagePhoto(id: string, photoUrl: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('construction_stages')
      .update({
        photo_url: photoUrl,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select();

    if (error) {
      return { data: null, error };
    }

    // Check if any rows were returned
    if (!data || data.length === 0) {
      return { data: null, error: new Error('No stage found with the provided ID') };
    }

    return { data: data[0], error: null };
  },

  // Create default construction stages for a new project
  async createDefaultStages(projectId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Construction phases with color mappings using translation keys
    const defaultPhases = {
      "progress_tab.phases.preparation": "bg-orange-500",
      "progress_tab.phases.foundation": "bg-blue-500", 
      "progress_tab.phases.structure": "bg-purple-500",
      "progress_tab.phases.walls_roof": "bg-green-500",
      "progress_tab.phases.installations": "bg-red-500",
      "progress_tab.phases.interior": "bg-yellow-500",
      "progress_tab.phases.finishing": "bg-cyan-500", 
      "progress_tab.phases.completion": "bg-purple-500"
    };

    // Default stages grouped by phase using translation keys
    const defaultStages = [
      // Preparation Phase
      { name: "progress_tab.stages.site_setup", phase: "progress_tab.phases.preparation", orderIndex: 1 },
      { name: "progress_tab.stages.excavation", phase: "progress_tab.phases.preparation", orderIndex: 2 },
      { name: "progress_tab.stages.soil_study", phase: "progress_tab.phases.preparation", orderIndex: 3 },
      { name: "progress_tab.stages.foundation_digging", phase: "progress_tab.phases.preparation", orderIndex: 4 },
      { name: "progress_tab.stages.concrete_pouring", phase: "progress_tab.phases.preparation", orderIndex: 5 },
      { name: "progress_tab.stages.waterproofing", phase: "progress_tab.phases.preparation", orderIndex: 6 },
      { name: "progress_tab.stages.rebar_placement", phase: "progress_tab.phases.preparation", orderIndex: 7 },
      
      // Foundation Phase
      { name: "progress_tab.stages.foundation_formwork", phase: "progress_tab.phases.foundation", orderIndex: 8 },
      { name: "progress_tab.stages.foundation_concrete", phase: "progress_tab.phases.foundation", orderIndex: 9 },
      { name: "progress_tab.stages.foundation_curing", phase: "progress_tab.phases.foundation", orderIndex: 10 },
      { name: "progress_tab.stages.foundation_inspection", phase: "progress_tab.phases.foundation", orderIndex: 11 },
      { name: "progress_tab.stages.foundation_insulation", phase: "progress_tab.phases.foundation", orderIndex: 12 },
      
      // Building Structure
      { name: "progress_tab.stages.basement_columns", phase: "progress_tab.phases.structure", orderIndex: 13 },
      { name: "progress_tab.stages.ground_floor", phase: "progress_tab.phases.structure", orderIndex: 14 },
      { name: "progress_tab.stages.upper_floors", phase: "progress_tab.phases.structure", orderIndex: 15 },
      { name: "progress_tab.stages.stair_formwork", phase: "progress_tab.phases.structure", orderIndex: 16 },
      { name: "progress_tab.stages.rough_construction", phase: "progress_tab.phases.structure", orderIndex: 17 },
      
      // Walls and Roof
      { name: "progress_tab.stages.brick_walls", phase: "progress_tab.phases.walls_roof", orderIndex: 18 },
      { name: "progress_tab.stages.slab_insulation", phase: "progress_tab.phases.walls_roof", orderIndex: 19 },
      { name: "progress_tab.stages.roof_truss", phase: "progress_tab.phases.walls_roof", orderIndex: 20 },
      { name: "progress_tab.stages.roof_covering", phase: "progress_tab.phases.walls_roof", orderIndex: 21 },
      { name: "progress_tab.stages.gutters", phase: "progress_tab.phases.walls_roof", orderIndex: 22 },
      
      // Installation Works
      { name: "progress_tab.stages.electrical_installation", phase: "progress_tab.phases.installations", orderIndex: 23 },
      { name: "progress_tab.stages.plumbing_installation", phase: "progress_tab.phases.installations", orderIndex: 24 },
      { name: "progress_tab.stages.gas_installation", phase: "progress_tab.phases.installations", orderIndex: 25 },
      { name: "progress_tab.stages.infrastructure_connections", phase: "progress_tab.phases.installations", orderIndex: 26 },
      { name: "progress_tab.stages.ventilation_ducts", phase: "progress_tab.phases.installations", orderIndex: 27 },
      
      // Interior Works
      { name: "progress_tab.stages.interior_plaster", phase: "progress_tab.phases.interior", orderIndex: 28 },
      { name: "progress_tab.stages.exterior_plaster", phase: "progress_tab.phases.interior", orderIndex: 29 },
      { name: "progress_tab.stages.thermal_insulation", phase: "progress_tab.phases.interior", orderIndex: 30 },
      { name: "progress_tab.stages.paint_primer", phase: "progress_tab.phases.interior", orderIndex: 31 },
      { name: "progress_tab.stages.ceramic_tiles", phase: "progress_tab.phases.interior", orderIndex: 32 },
      { name: "progress_tab.stages.parquet_flooring", phase: "progress_tab.phases.interior", orderIndex: 33 },
      
      // Final Systems and Finishing
      { name: "progress_tab.stages.interior_doors", phase: "progress_tab.phases.finishing", orderIndex: 34 },
      { name: "progress_tab.stages.window_installation", phase: "progress_tab.phases.finishing", orderIndex: 35 },
      { name: "progress_tab.stages.electrical_fixtures", phase: "progress_tab.phases.finishing", orderIndex: 36 },
      { name: "progress_tab.stages.plumbing_fixtures", phase: "progress_tab.phases.finishing", orderIndex: 37 },
      { name: "progress_tab.stages.final_painting", phase: "progress_tab.phases.finishing", orderIndex: 38 },
      { name: "progress_tab.stages.balcony_terrace", phase: "progress_tab.phases.finishing", orderIndex: 39 },
      { name: "progress_tab.stages.kitchen_cabinets", phase: "progress_tab.phases.finishing", orderIndex: 40 },
      { name: "progress_tab.stages.hvac_installation", phase: "progress_tab.phases.finishing", orderIndex: 41 },
      
      // Completion and Delivery
      { name: "progress_tab.stages.final_cleaning", phase: "progress_tab.phases.completion", orderIndex: 42 },
      { name: "progress_tab.stages.owner_inspection", phase: "progress_tab.phases.completion", orderIndex: 43 },
      { name: "progress_tab.stages.deficiency_repairs", phase: "progress_tab.phases.completion", orderIndex: 44 },
      { name: "progress_tab.stages.ready_for_handover", phase: "progress_tab.phases.completion", orderIndex: 45 },
      { name: "progress_tab.stages.handover", phase: "progress_tab.phases.completion", orderIndex: 46 }
    ];

    // Insert all default stages
    try {
      // First check if order_index column exists in the table
      try {
        const { data: stagesToInsert, error } = await supabase
          .from('construction_stages')
          .insert({
            project_id: projectId,
            name: 'Test Stage',
            completed: false,
            phase: 'Test Phase',
            phase_color: 'bg-gray-500',
            order_index: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();
          
        if (error && error.message.includes('order_index does not exist')) {
          // If order_index doesn't exist, insert without it
          const stagesToInsertWithoutOrderIndex = defaultStages.map(stage => ({
            project_id: projectId,
            name: stage.name,
            completed: false,
            phase: stage.phase,
            phase_color: defaultPhases[stage.phase],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));
          
          const { data, error: insertError } = await supabase
            .from('construction_stages')
            .insert(stagesToInsertWithoutOrderIndex)
            .select();
            
          // Clean up the test record
          await supabase
            .from('construction_stages')
            .delete()
            .eq('name', 'Test Stage');
            
          return { data, error: insertError };
        } else {
          // If order_index exists, delete the test record
          await supabase
            .from('construction_stages')
            .delete()
            .eq('name', 'Test Stage');
            
          // Insert all stages with order_index
          const stagesToInsert = defaultStages.map(stage => ({
            project_id: projectId,
            name: stage.name,
            completed: false,
            phase: stage.phase,
            phase_color: defaultPhases[stage.phase],
            order_index: stage.orderIndex,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));
          
          const { data, error: insertError } = await supabase
            .from('construction_stages')
            .insert(stagesToInsert)
            .select();
            
          return { data, error: insertError };
        }
      } catch (err) {
        
        
        // Fallback to inserting without order_index
        const stagesToInsertWithoutOrderIndex = defaultStages.map(stage => ({
          project_id: projectId,
          name: stage.name,
          completed: false,
          phase: stage.phase,
          phase_color: defaultPhases[stage.phase],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        const { data, error: insertError } = await supabase
          .from('construction_stages')
          .insert(stagesToInsertWithoutOrderIndex)
          .select();
          
        return { data, error: insertError };
      }
    } catch (err) {
      
      return { data: null, error: err as Error };
    }
  }
}; 